import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { TopNavActionProvider, useTopNavAction } from "@/contexts/TopNavActionContext";
import TopNavSearch from "@/components/TopNavSearch";
import BannerHost from "@/components/BannerHost";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  GitBranch,
  Link2,
  GraduationCap,
  BarChart3,
  Settings,
  Shield,
  ClipboardList,
  Layers,
  Puzzle,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Monitor,
  Network,
  MessageSquare,
  NotebookPen,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const navItems = [
  { label: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Classroom", path: "/admin/classroom", icon: Monitor },
  { section: "System" },
  { label: "Analytics", path: "/admin/analytics", icon: BarChart3, requireFeature: "analytics" },
  { label: "Learning Logic", path: "/admin/logic", icon: GitBranch, requireFeature: "learning_logic" },
  { label: "Admin Users", path: "/admin/users", icon: Shield, requireRole: "system_admin" },
  { label: "Global Settings", path: "/admin/settings", icon: Settings },
  { label: "Sandbox", path: "/admin/epic-practice", icon: Monitor, requireFeature: "epic_practice" },
  { section: "Learner Management" },
  { label: "Cohort Management", path: "/admin/cohorts", icon: Users, requireFeature: "learner_management" },
  { label: "Learners", path: "/admin/learners", icon: GraduationCap, requireFeature: "learner_management" },
  { label: "Events", path: "/admin/events", icon: Calendar, requireFeature: "learner_management" },
  { section: "Curriculum" },
  { label: "Curriculum Manager", path: "/admin/curriculum", icon: Network, requireFeature: "curriculum" },
  { label: "Track Library", path: "/admin/tracks", icon: Layers, requireFeature: "curriculum" },
  { label: "Learning Experience Library", path: "/admin/learning-experiences", icon: BookOpen, requireFeature: "curriculum" },
  { label: "Lesson Planner", path: "/admin/lesson-plans", icon: NotebookPen, requireFeature: "lesson_planner" },
  { label: "Historical Assessments", path: "/admin/assessment-types", icon: ClipboardList, requireFeature: "historical_assessments" },
  { label: "Survey Library", path: "/admin/surveys", icon: MessageSquare, requireFeature: "surveys" },
  { label: "Question Bank", path: "/admin/items", icon: Puzzle, requireFeature: "question_bank" },
  { label: "Resource Library", path: "/admin/resources", icon: Link2, requireFeature: "resource_library" },
];

function getSectionTitle(pathname, dynamicItems) {
  if (pathname === "/admin/profile") return "Profile Settings";
  const item = dynamicItems.find((i) => pathname.startsWith(i.path));
  return item ? item.label : "Dashboard";
}

export default function AdminLayout() {
  return (
    <TopNavActionProvider>
      <AdminLayoutInner />
    </TopNavActionProvider>
  );
}

function AdminLayoutInner() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { action: topNavAction } = useTopNavAction();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Accordion navigation — only one section open at a time. On initial load all
  // sections are collapsed; on mount and on route change, the section containing
  // the active nav item is auto-expanded so the current page is always visible.
  const getActiveSection = (pathname, items) => {
    let currentSection = null;
    for (const item of items) {
      if (item.section) { currentSection = item.section; continue; }
      if (item.path && pathname.startsWith(item.path)) return currentSection;
    }
    return null;
  };
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const firstName = user?.full_name?.split(" ")[0] || "Admin";

  const featureFlags = user?.feature_flags || {};
  // If the user doc hasn't hydrated feature_flags yet (e.g. the old /me was
  // cached), default to "enabled" so we don't hide nav for the SA on the
  // dinaxlp tenant. A second render lands once the new /me response arrives.
  const featureEnabled = (key) => !key || featureFlags[key] !== false;
  const hiddenFeatures = new Set(user?.hidden_features || []);
  // Per-user hidden features (SA-managed) — cosmetic hide only. Underlying
  // RBAC is unchanged; these keys match the same feature flags used for
  // tier gating, so reusing `requireFeature` is safe.
  const userFeatureEnabled = (key) => !key || !hiddenFeatures.has(key);

  const dynamicNavItems = navItems
    .filter((item) => item.section || !item.requireRole || user?.role === item.requireRole)
    .filter((item) => item.section || featureEnabled(item.requireFeature))
    .filter((item) => item.section || userFeatureEnabled(item.requireFeature))
    // Hide any section header that now has zero visible items below it.
    .filter((item, idx, arr) => {
      if (!item.section) return true;
      for (let j = idx + 1; j < arr.length; j++) {
        if (arr[j].section) return false;
        if (arr[j].path) return true;
      }
      return false;
    })
    .map((item) => {
      if (item.label === "Learners") {
        return {
          ...item,
          label: `${settings.learner_label || "Learner"} Management`,
        };
      }
      if (item.path === "/admin/epic-practice") {
        const custom = (user?.epic_practice_label || "").trim();
        if (custom) return { ...item, label: custom };
      }
      return item;
    });

  // Auto-expand the section containing the active page on mount and whenever
  // the route changes. If the active item is ungrouped (top items like
  // Dashboard/Classroom), nothing is opened — all sections stay collapsed.
  useEffect(() => {
    const s = getActiveSection(location.pathname, dynamicNavItems);
    if (s) setOpenSection(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sectionTitle = getSectionTitle(location.pathname, dynamicNavItems);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--admin-bg, #0A0C10)" }} data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 z-40"
        style={{
          backgroundColor: "var(--admin-sidebar-bg, #0A0C10)",
          // Accessibility overlay — applied only when raw primary/text
          // contrast falls below AA (e.g. Pulse Red, Monocle Yellow).
          // `--shell-overlay` is `transparent` otherwise, so this line is
          // a no-op for safe themes.
          backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))",
          borderRight: "1px solid var(--admin-sidebar-border)",
        }}
        data-testid="admin-sidebar"
      >
        <div className="h-20 flex flex-col justify-center px-5 shrink-0" style={{ borderBottom: "1px solid var(--admin-sidebar-border)" }} data-testid="sidebar-brand">
          <h1 className="font-heading text-lg font-bold tracking-tight leading-tight truncate" style={{ color: "var(--admin-sidebar-text)" }} data-testid="sidebar-org-name">
            {user?.org_name || "Organization"}
          </h1>
          <span className="text-[10px] font-body uppercase tracking-wider mt-0.5" style={{ color: "var(--admin-sidebar-muted)", opacity: 0.7 }} data-testid="sidebar-powered-by">
            Powered by DiNAXLP
          </span>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {(() => {
            let currentSection = null;
            return dynamicNavItems.map((item, idx) => {
              if (item.section) {
                currentSection = item.section;
                const isOpen = openSection === item.section;
                return (
                  <div key={`section-${item.section}`} className={`${idx > 0 ? "mt-1" : ""}`}>
                    <button
                      onClick={() => toggleSection(item.section)}
                      className="w-full px-3 py-2.5 text-left cursor-pointer select-none rounded-lg transition-colors duration-150 hover:bg-white/5"
                      style={{ borderLeft: isOpen ? "2px solid var(--shell-section-label)" : "2px solid transparent" }}
                      data-testid={`nav-section-${item.section.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <span
                        className="text-xs font-body font-bold uppercase tracking-wider"
                        style={{
                          letterSpacing: "0.08em",
                          color: "var(--shell-section-label)",
                          // Open section uses the theme-driven section-label
                          // opacity; collapsed dims further by 30% so the
                          // current context stays salient.
                          opacity: isOpen
                            ? `var(--shell-section-label-opacity, 1)`
                            : `calc(var(--shell-section-label-opacity, 1) * 0.7)`,
                        }}
                      >
                        {item.section}
                      </span>
                    </button>
                  </div>
                );
              }
              if (currentSection && openSection !== currentSection) return null;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                      isActive ? "nav-active font-body font-bold" : "font-body font-medium"
                    }`
                  }
                  style={({ isActive }) => ({
                    color: "var(--shell-text)",
                    opacity: isActive ? 1 : "var(--shell-text-muted-opacity, 0.65)",
                    borderLeft: isActive
                      ? `var(--shell-active-border-width, 3px) solid var(--shell-section-label)`
                      : `var(--shell-active-border-width, 3px) solid transparent`,
                    paddingLeft: "calc(0.75rem)",
                    paddingRight: "0.75rem",
                    backgroundColor: "transparent",
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className="h-4 w-4 shrink-0"
                        style={{ color: isActive ? "var(--shell-active-icon-color, var(--shell-text))" : "inherit" }}
                      />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            });
          })()}
        </nav>

        <div className="p-4 shrink-0" style={{ borderTop: "1px solid var(--admin-sidebar-border)" }}>
          <div
            className="flex items-center gap-3 px-3 cursor-pointer rounded-lg py-1.5 transition-colors"
            onClick={() => navigate("/admin/profile")}
            data-testid="sidebar-profile-link"
          >
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {firstName[0]}
            </div>
            <div className="min-w-0">
              <p
                className="text-sm font-body font-medium truncate"
                style={{ color: "var(--admin-sidebar-text)" }}
                data-testid="sidebar-user-name"
              >
                {user?.full_name}
              </p>
              <p className="text-xs font-body capitalize" style={{ color: "var(--admin-sidebar-muted)" }}>
                {user?.role?.replace("_", " ")}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0">
        {/* Top Nav — sticky on desktop, fixed on mobile so it survives
            the global overflow-x: hidden we apply to prevent horizontal
            page overflow. */}
        <header
          className="fixed lg:sticky top-0 left-0 right-0 lg:left-auto lg:right-auto z-50 h-16 flex items-center justify-between px-4 sm:px-6 backdrop-blur-xl"
          style={{ backgroundColor: "var(--admin-nav-bg, #0A0C10)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderBottom: "1px solid var(--admin-nav-border)" }}
          data-testid="admin-top-nav"
        >
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden transition-colors"
              style={{ color: "var(--shell-text-muted, var(--admin-nav-muted))" }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
            <span
              className="md:hidden text-sm font-heading font-semibold"
              style={{ color: "var(--admin-nav-text)" }}
              data-testid="topnav-section-name-mobile"
            >
              {sectionTitle}
            </span>
          </div>

          <div className="flex items-center gap-4 flex-1 ml-0 md:ml-6">
            <span
              className="hidden md:block text-sm font-body whitespace-nowrap"
              style={{
                color: "var(--shell-text)",
                fontWeight: "var(--topnav-breadcrumb-weight, 500)",
              }}
              data-testid="topnav-section-name"
            >
              {sectionTitle}
            </span>

            <TopNavSearch />
          </div>

          <div className="flex items-center gap-3">
            {/* Contextual action slot — pages push a Save / Publish button
                here via `useTopNavAction().setAction(...)`. Empty when
                the current page hasn't registered one. */}
            {topNavAction && (
              <div data-testid="topnav-action-slot">{topNavAction}</div>
            )}
            {settings.help_email && (
              <a
                href={`mailto:${settings.help_email}`}
                className="hidden sm:flex items-center gap-1.5 text-sm transition-colors font-body"
                style={{ color: "var(--shell-text-muted, var(--admin-nav-muted))" }}
                data-testid="topnav-help-link"
              >
                <HelpCircle className="h-4 w-4" />
                <span>Need Help</span>
              </a>
            )}
            <span
              className="hidden sm:block text-sm font-body"
              style={{ color: "var(--shell-text-muted, var(--admin-nav-muted))" }}
              data-testid="topnav-user-name"
            >
              {user?.full_name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="topnav-logout-button"
              className="hover:text-red-400 hover:bg-red-500/10"
              style={{ color: "var(--shell-text-muted, var(--admin-nav-muted))" }}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Button>
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 pt-20 lg:pt-6 pb-24 lg:pb-6 overflow-x-hidden" style={{ backgroundColor: "var(--admin-bg, #0A0C10)" }}>
          <BannerHost />
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 p-4 overflow-y-auto" style={{ backgroundColor: "var(--admin-sidebar-bg, #0A0C10)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderRight: "1px solid var(--admin-sidebar-border)" }}>
            <div className="flex items-start justify-between mb-6">
              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-base font-bold tracking-tight truncate leading-tight" style={{ color: "var(--admin-sidebar-text)" }}>
                  {user?.org_name || "Organization"}
                </h1>
                <span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "var(--admin-sidebar-muted)", opacity: 0.7 }}>
                  Powered by DiNAXLP
                </span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{ color: "var(--admin-sidebar-muted)" }}
                data-testid="mobile-sidebar-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="space-y-1">
              {(() => {
                let currentSection = null;
                return dynamicNavItems.map((item, idx) => {
                  if (item.section) {
                    currentSection = item.section;
                    const isOpen = openSection === item.section;
                    return (
                      <div key={`section-${item.section}`} className={`${idx > 0 ? "mt-1" : ""}`}>
                        <button
                          onClick={() => toggleSection(item.section)}
                          className="w-full px-3 py-2.5 text-left cursor-pointer select-none rounded-lg transition-colors duration-150 hover:bg-white/5"
                          style={{ borderLeft: isOpen ? "2px solid var(--shell-section-label)" : "2px solid transparent" }}
                        >
                          <span
                            className="text-xs font-body font-bold uppercase tracking-wider"
                            style={{ letterSpacing: "0.08em", color: "var(--shell-section-label)", opacity: isOpen ? 1 : 0.7 }}
                          >
                            {item.section}
                          </span>
                        </button>
                      </div>
                    );
                  }
                  if (currentSection && openSection !== currentSection) return null;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`mobile-nav-${item.path.split("/").pop()}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                          isActive ? "font-body font-bold" : "font-body font-medium"
                        }`
                      }
                      style={({ isActive }) => ({
                        color: "var(--shell-text)",
                        opacity: isActive ? 1 : 0.65,
                        borderLeft: isActive ? "3px solid var(--shell-section-label)" : "3px solid transparent",
                        paddingLeft: isActive ? "calc(0.75rem - 3px)" : "0.75rem",
                        paddingRight: "0.75rem",
                        backgroundColor: "transparent",
                      })}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                });
              })()}
            </nav>
          </aside>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 backdrop-blur-lg z-40 flex justify-around py-2 px-1"
        style={{ backgroundColor: "var(--admin-nav-bg, #0A0C10)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderTop: "1px solid var(--admin-nav-border)" }}
        data-testid="mobile-bottom-nav"
      >
        {dynamicNavItems.filter((i) => !i.section).filter((i) => 
          ["Dashboard", "Cohort Management", "Events", `${settings.learner_label || "Learner"} Management`, "Analytics"].includes(i.label)
        ).slice(0, 5).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`bottom-nav-${item.path.split("/").pop()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-1 px-2 text-[10px] font-body rounded-lg transition-colors`
            }
            style={({ isActive }) => ({
              color: "var(--shell-text)",
              opacity: isActive ? 1 : 0.65,
              fontWeight: isActive ? 700 : 500,
            })}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
